import React, { useState } from 'react';
import './IntegrationInfoCard.css';

type IntegrationInfoCardProps = {
  imageSrc: string;
  title?: string;
  description: React.ReactNode; // can include links/HTML
  learnMoreLabel?: string;
  disable: boolean;
  copyContent?: string; // optional code/url to copy on back side
  orgID?: string;
  orgName?: string;
  accessAllowed?: boolean; // if false, hide code and show request-access UI
  optionAComplete?: boolean; // if false, block request-access until the Option A form is filled
  onCustomizeOptionA?: () => void; // opens the Option A customize modal
};

const IntegrationInfoCard: React.FC<IntegrationInfoCardProps> = ({
  imageSrc,
  title,
  description,
  learnMoreLabel = 'Learn more',
  copyContent,
  disable
  ,orgID, orgName, accessAllowed = true, optionAComplete = true, onCustomizeOptionA
}) => {
  const [flipped, setFlipped] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!copyContent) return;
    try {
      await navigator.clipboard.writeText(copyContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { }
  };

  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const handleRequestAccess = async () => {
    if (!orgID) return;
    if (!optionAComplete) return;
    setRequesting(true);
    try {
      const res = await fetch('/api/branding/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgID, orgName }),
      });
      if (res.ok) {
        setRequested(true);
      }
    } catch (e) {
      // ignore
    }
    setRequesting(false);
  };

  return (
      <div className={`${disable ? "disable" : ""} ni-card`}>
      {disable ? 
      <span className="ribbon">Coming Soon</span> : null
      }
        <div className={`ni-card-inner ${flipped ? 'is-flipped' : ''}`}>
          {/* FRONT */}
          <div className="ni-card-face ni-card-front">
            {title ? <h3 className="ni-card-title">{title}</h3> : null}
            <div className="ni-card-image-wrap">
              <img className="ni-card-image" src={imageSrc} alt={typeof title === 'string' ? title : 'Card'} />
            </div>
            <button className="ni-card-btn" onClick={() => setFlipped(true)}>{learnMoreLabel}</button>
          </div>

          {/* BACK */}
          <div className="ni-card-face ni-card-back">
            <div className="ni-card-back-content">
              {typeof description === 'string' ? (
                <div dangerouslySetInnerHTML={{ __html: description as string }} />
              ) : (
                description
              )}
              {accessAllowed === false ? (
                <div className="ni-card-copybox ni-card-access">
                  <div className="ni-card-access-center">
                    {!requested ? (
                      !optionAComplete ? (
                        <>
                          <p style={{ margin: '0 0 10px 0' }}>
                            Customize your checkout box and add at least one allowed website domain before requesting access.
                          </p>
                          <button className="ni-request-btn" onClick={onCustomizeOptionA}>
                            Customize checkout box
                          </button>
                        </>
                      ) : (
                        <>
                          <p style={{ margin: '0 0 10px 0' }}>Request access to enable API on your site.</p>
                          <button className="ni-request-btn" onClick={handleRequestAccess} disabled={requesting}>
                            {requesting ? 'Sending…' : 'Request Access'}
                          </button>
                        </>
                      )
                    ) : (
                      <div className="ni-request-success">
                        {/* <strong>Request sent — we'll connect with you shortly.</strong> */}
                        <div style={{ marginTop: 8, fontSize: 13 }}>Thank you — we'll notify you when access is enabled.</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                copyContent && (
                  <div className="ni-card-copybox">
                    <div className="ni-card-copy-top">
                      <span className={`ni-card-copied ${copied ? 'is-on' : ''}`}>✓ Copied</span>
                      <button className="ni-card-copy-btn" onClick={handleCopy}>Copy code</button>
                    </div>
                    <pre className="ni-card-pre">{copyContent}</pre>
                  </div>
                )
              )}
            </div>
            <button className="ni-card-btn" onClick={() => setFlipped(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
    </div>
  );
};

export default IntegrationInfoCard;